import React, { useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, useMap } from 'react-leaflet';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faExpand, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useEffect } from 'react';
import { createMapPinIcon } from '../../../coffee_shops/components/MapPin';
import { CoffeeMapBasemap, COFFEE_MAP_CONTAINER_CLASS } from '../../../coffee_shops/components/CoffeeMapBasemap';
import type { Coordinates } from '../types';

const InvalidateOnMount: React.FC = () => {
    const map = useMap();
    useEffect(() => {
        const t = window.setTimeout(() => map.invalidateSize(), 100);
        return () => window.clearTimeout(t);
    }, [map]);
    return null;
};

interface CardActionBarProps {
    onToggleStats: () => void;
    onToggleMap?: () => void;
    statsActive: boolean;
    mapActive: boolean;
    canShowMap: boolean;
    overallRating: number;
    mapCoordinates: Coordinates;
}

/**
 * Bottom-right action bar over the image. Always visible (z-index above
 * stats/map overlays). Each button toggles its own view from the same spot:
 * click again to close. Stats button shows chevron; map thumb shows a tiny
 * Leaflet preview and gets `is-active` styling when the map view is open.
 */
export const CardActionBar: React.FC<CardActionBarProps> = ({
    onToggleStats,
    onToggleMap,
    statsActive,
    mapActive,
    canShowMap,
    overallRating,
    mapCoordinates,
}) => {
    const thumbIcon = useMemo(() => {
        const size = 18;
        return L.icon({
            iconUrl: createMapPinIcon(overallRating ?? 0, false),
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2],
        });
    }, [overallRating]);

    return (
        <div className='image-section-toolbar' role='toolbar' aria-label='Coffee card view options'>
            <button
                type='button'
                className={`toggle-button${statsActive ? ' is-active' : ''}`}
                onClick={onToggleStats}
                aria-pressed={statsActive}
            >
                <FontAwesomeIcon icon={statsActive ? faChevronUp : faChevronDown} />
                {statsActive ? 'Hide Stats' : 'Show Stats'}
            </button>
            {canShowMap && onToggleMap && (
                <motion.button
                    type='button'
                    className={`coffee-map-thumb-launch${mapActive ? ' is-active' : ''}`}
                    onClick={onToggleMap}
                    aria-label={mapActive ? 'Close location map' : 'Expand location map'}
                    aria-pressed={mapActive}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    layout
                >
                    <span className='coffee-map-thumb-frame'>
                        <MapContainer
                            className={`${COFFEE_MAP_CONTAINER_CLASS} map-thumb-leaflet`}
                            center={[mapCoordinates.lat, mapCoordinates.lng]}
                            zoom={13}
                            dragging={false}
                            doubleClickZoom={false}
                            scrollWheelZoom={false}
                            zoomControl={false}
                            attributionControl={false}
                            keyboard={false}
                        >
                            <CoffeeMapBasemap />
                            <Marker position={[mapCoordinates.lat, mapCoordinates.lng]} icon={thumbIcon} />
                            <InvalidateOnMount />
                        </MapContainer>
                        <span className='coffee-map-thumb-badge' aria-hidden>
                            <FontAwesomeIcon icon={mapActive ? faXmark : faExpand} />
                        </span>
                    </span>
                </motion.button>
            )}
        </div>
    );
};
